import { z } from 'zod';
import { nonEmptyPatch } from '../_patch.js';

// Cargo registry dictionaries (#1236): two flat reference directories —
// CargoCarrierOption ("Перевозчик") and CargoPathFormatOption ("Формат пути").
// Both feed the free-string `CargoArticle.carrier` / `CargoArticle.pathFormat`
// columns (string-choice, NOT FK). The two resources share an identical wire
// shape, so the schemas derive from a shared base; exported names stay distinct
// and explicit per resource. Mirrors RoadOption / PackagingOption (#995).

const trimmedNonEmpty = z
  .string()
  .min(1)
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, 'must not be blank');

const stringBoolean = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => (typeof v === 'boolean' ? v : v === 'true'));

const optionItemShape = {
  id: z.string().uuid(),
  name: trimmedNonEmpty,
  active: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
};

const createInputShape = {
  name: trimmedNonEmpty,
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).default(0),
};

const patchInputShape = {
  name: trimmedNonEmpty,
  sortOrder: z.number().int().min(0),
};

const listQueryShape = {
  q: z.string().min(1).max(200).optional(),
  active: stringBoolean.optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
};

/**
 * @aiPurpose canonical wire shape for a Cargo carrier option row (#1236).
 *   Selectable carrier strings for `CargoArticle.carrier`; archived rows drop
 *   out of active pickers but historical strings stay displayable.
 * @aiInvariant `name` is trimmed and rejected when blank. DB enforces
 *   active-only normalized uniqueness via a partial unique index on
 *   `lower(name) WHERE active = true` (declared in
 *   `20260601231530_add_cargo_dictionaries`); restoring an archived duplicate
 *   while an active duplicate exists surfaces `DOMAIN_CONFLICT`. List ordering
 *   is `(sortOrder ASC, id ASC)`.
 * @aiSecurity none — reference data, no PII surface.
 */
export const CargoCarrierOptionSchema = z.object(optionItemShape).strict();
export type CargoCarrierOption = z.infer<typeof CargoCarrierOptionSchema>;

/**
 * @aiPurpose `POST /api/cargo/carrier-options` body. `active` defaults to
 *   `true`, `sortOrder` defaults to `0`.
 */
export const CreateCargoCarrierOptionInputSchema = z.object(createInputShape).strict();
export type CreateCargoCarrierOptionInput = z.infer<typeof CreateCargoCarrierOptionInputSchema>;

/**
 * @aiPurpose `PATCH /api/cargo/carrier-options/:id` body. `active` is reserved
 *   for the dedicated archive/activate endpoints; the patch shape intentionally
 *   only exposes business fields (`name`, `sortOrder`).
 */
export const PatchCargoCarrierOptionInputSchema = nonEmptyPatch(
  z.object(patchInputShape).partial().strict(),
);
export type PatchCargoCarrierOptionInput = z.infer<typeof PatchCargoCarrierOptionInputSchema>;

/**
 * @aiPurpose query string for `GET /api/cargo/carrier-options`. `q` matches
 *   `name` with case-insensitive substring semantics.
 * @aiInvariant `.strict()` rejects unknown keys. `limit` defaults to `50`, max
 *   `500`. `active` defaults to undefined so callers explicitly opt into the
 *   active/archived view.
 */
export const GetCargoCarrierOptionsQuerySchema = z.object(listQueryShape).strict();
export type GetCargoCarrierOptionsQuery = z.infer<typeof GetCargoCarrierOptionsQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/cargo/carrier-options`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying query
 *   returned ≤ `limit` rows; otherwise it carries the encoded `(sortOrder, id)`
 *   of the last returned row.
 */
export const GetCargoCarrierOptionsResponseSchema = z
  .object({
    items: z.array(CargoCarrierOptionSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetCargoCarrierOptionsResponse = z.infer<typeof GetCargoCarrierOptionsResponseSchema>;

/**
 * @aiPurpose canonical wire shape for a Cargo path-format option row (#1236).
 *   Selectable path-format strings for `CargoArticle.pathFormat`; same
 *   string-choice (not FK) and archive semantics as `CargoCarrierOption`.
 * @aiInvariant active-name uniqueness via the partial unique index
 *   `lower(name) WHERE active = true`. List ordering is `(sortOrder ASC, id ASC)`.
 * @aiSecurity none — reference data, no PII surface.
 */
export const CargoPathFormatOptionSchema = z.object(optionItemShape).strict();
export type CargoPathFormatOption = z.infer<typeof CargoPathFormatOptionSchema>;

/**
 * @aiPurpose `POST /api/cargo/path-format-options` body. `active` defaults to
 *   `true`, `sortOrder` defaults to `0`.
 */
export const CreateCargoPathFormatOptionInputSchema = z.object(createInputShape).strict();
export type CreateCargoPathFormatOptionInput = z.infer<
  typeof CreateCargoPathFormatOptionInputSchema
>;

/**
 * @aiPurpose `PATCH /api/cargo/path-format-options/:id` body. `active` is
 *   reserved for the dedicated archive/activate endpoints.
 */
export const PatchCargoPathFormatOptionInputSchema = nonEmptyPatch(
  z.object(patchInputShape).partial().strict(),
);
export type PatchCargoPathFormatOptionInput = z.infer<typeof PatchCargoPathFormatOptionInputSchema>;

/**
 * @aiPurpose query string for `GET /api/cargo/path-format-options`. `q` matches
 *   `name` with case-insensitive substring semantics.
 * @aiInvariant `.strict()` rejects unknown keys. `limit` defaults to `50`, max
 *   `500`. `active` defaults to undefined.
 */
export const GetCargoPathFormatOptionsQuerySchema = z.object(listQueryShape).strict();
export type GetCargoPathFormatOptionsQuery = z.infer<typeof GetCargoPathFormatOptionsQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/cargo/path-format-options`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying query
 *   returned ≤ `limit` rows; otherwise the encoded `(sortOrder, id)` of the
 *   last returned row.
 */
export const GetCargoPathFormatOptionsResponseSchema = z
  .object({
    items: z.array(CargoPathFormatOptionSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetCargoPathFormatOptionsResponse = z.infer<
  typeof GetCargoPathFormatOptionsResponseSchema
>;
